import { Realtime } from 'ably';
import axios from 'axios';
import { signOut } from 'firebase/auth';
import { SITE_SERVICE } from '../common/constants';
import { primaryAuth } from '../firebase';

/** Ably client is stored outside the React lifecycle in browser memory */
let clientInstance = null;

/** Helper to force clean session termination and avoid redirect loops */
const handleForcedLogout = async (reason) => {
  console.warn(`🚨 [SESSION TERMINATED] ${reason}. Redirecting to login...`);

  // 1. wipe local client state
  try {
    await signOut(primaryAuth);
  } catch {
    /* suppress errors during cleanup */
  }

  // 2. only redirect if not already on login page
  if (window.location.pathname !== '/signIn') {
    // optional: save return URL so the user comes back here after re-login
    sessionStorage.setItem('redirect_after_login', window.location.pathname);
    // window.location.href = '/signIn';
  }
};

/**
 * Creates and maintains an Ably client singleton outside the React render loop,
 * from which all door channels are multiplexed (1 shared WebSocket).
 *
 * - `getPortalAblyClient()` runs once and creates a single connection instance
 * - `<AblyProvider client={ablyClient}>` broadcasts that instance down the
 * component tree
 * - each button executes `const ably = useAbly()` -> shared `ably` reference
 * - each button's `doorControl` hook listens for events on its own channel
 *
 * @param {string} siteId The site's unique identifier
 * @returns
 */
export const getPortalAblyClient = async (siteId) => {
  // reuse an active connection if it already exists (prevent duplicate sockets)
  if (
    clientInstance &&
    !['closed', 'failed'].includes(clientInstance.connection.state)
  ) {
    return clientInstance;
  }

  const uid = primaryAuth.currentUser?.uid ?? 'guest';
  const portalId = `portal:${siteId}:${uid}`; // for dashboard identification

  clientInstance = new Realtime({
    authCallback: async (data, callback) => {
      try {
        const user = primaryAuth.currentUser;

        // 1. NO USER DETECTED: force logout immediately
        if (!user) {
          await handleForcedLogout('No active Firebase user found');
          const err = new Error('Session expired. Redirecting to login.');
          return callback(err, null);
        }

        // 2. REFRESH TOKEN: throws if account is disabled/revoked
        let freshIdToken;
        try {
          freshIdToken = await user.getIdToken();
        } catch (tokenErr) {
          // triggers if admin revoked token or deleted the user
          await handleForcedLogout('Firebase token refresh rejected');
          return callback(tokenErr, null);
        }

        // 3. FETCH PORTAL TOKEN FROM BACKEND
        const response = await axios.post(
          `${SITE_SERVICE}/token/web-portal`,
          { clientId: portalId },
          {
            headers: { Authorization: `Bearer ${freshIdToken}` },
            responseType: 'text',
          },
        );

        // 4. pass the JWT directly to Ably
        return callback(null, response.data);
      } catch (error) {
        console.error('❌ [ABLY AUTH] Error:', error);
        if (error.response?.status === 401 || error.response?.status === 403) {
          await handleForcedLogout(
            `Backend rejected user session HTTP ${error.response.status}`,
          );
        }
        return callback(error, null); // passing the error halts Ably retries
      }
    },
  });

  return clientInstance;
};

/** In-memory door map, fetched once per session */
let cachedDoorsMap = null;
let cachedSiteId = null;

/**
 * Fetches the door map once per session. Re-reads only if `siteId` changes or
 * cache is explicitly cleared.
 *
 * Door states (ready/in-use/offline) are still streamed live over the Ably
 * WebSocket, so connection drops and countdowns remain real-time.
 *
 * @param {string} siteId The site's unique identifier
 * @param {boolean} [forceRefresh=false] (Optional) Force a cache rebuild,
 * overwriting existing data. Default is `false`
 * @returns {object} The site door map, or an empty object if it does not exist
 */
export const getDoorList = async (siteId, forceRefresh = false) => {
  // 1. Return from memory if already fetched for this site (unless overridden)
  if (cachedDoorsMap && cachedSiteId === siteId && !forceRefresh) {
    return cachedDoorsMap;
  }

  // 2. Otherwise, fetch from database
  const idToken = await primaryAuth.currentUser.getIdToken();
  const response = await axios.get(`${SITE_SERVICE}/doors/list`, {
    headers: { Authorization: `Bearer ${idToken}` },
    params: { siteId },
  });

  // 3. Cache results to skip subsequent database reads
  cachedSiteId = siteId;
  cachedDoorsMap = response.data.doorList ?? {};
  return cachedDoorsMap;
};

/** Optional helper to clear door cache */
export const clearDoorCache = () => {
  cachedDoorsMap = null;
  cachedSiteId = null;
};
